import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { STORE_OPTIONS } from '../constants/stores';
import userService from '../services/user.service';

interface StoreOption {
    value: string;
    label: string;
}

interface StoreContextValue {
    activeStoreId: string;
    assignedStoreId: string;
    canSwitchStores: boolean;
    isLoading: boolean;
    switchStore: (storeId: string) => void;
    resetToAssignedStore: () => void;
    storeOptions: StoreOption[];
}

interface StoreProviderProps {
    children: ReactNode;
    userRoles?: string[];
    assignedStoreId?: string;
}

const StoreContext = createContext<StoreContextValue | null>(null);

const STORAGE_KEY = 'selectedStoreId';
const STORE_SWITCHER_ROLES = ['dev', 'owner'];

export function StoreProvider({ children, userRoles = [], assignedStoreId = '' }: StoreProviderProps) {
    const [activeStoreId, setActiveStoreId] = useState<string>(assignedStoreId);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const canSwitchStores = userRoles.some(role =>
        STORE_SWITCHER_ROLES.includes(role.toLowerCase())
    );

    useEffect(() => {
        if (!assignedStoreId) {
            setIsLoading(false);
            return;
        }

        let selectedStore = assignedStoreId;

        if (canSwitchStores) {
            const savedStore = localStorage.getItem(STORAGE_KEY);
            const isValidStore = STORE_OPTIONS.some(s => s.value === savedStore);
            if (savedStore && isValidStore) {
                selectedStore = savedStore;
            } else {
                localStorage.removeItem(STORAGE_KEY);
            }
        }

        setActiveStoreId(selectedStore);
        userService.setStoreOverride(canSwitchStores ? selectedStore : null);
        setIsLoading(false);
    }, [assignedStoreId, canSwitchStores]);

    const switchStore = useCallback((newStoreId: string) => {
        if (!canSwitchStores) return;

        const isValidStore = STORE_OPTIONS.some(s => s.value === newStoreId);
        if (!isValidStore) {
            console.warn('Invalid store ID:', newStoreId);
            return;
        }

        setActiveStoreId(newStoreId);
        localStorage.setItem(STORAGE_KEY, newStoreId);
        userService.setStoreOverride(newStoreId);
    }, [canSwitchStores]);

    const resetToAssignedStore = useCallback(() => {
        setActiveStoreId(assignedStoreId);
        localStorage.removeItem(STORAGE_KEY);
        userService.clearStoreOverride();
    }, [assignedStoreId]);

    const value: StoreContextValue = {
        activeStoreId,
        assignedStoreId,
        canSwitchStores,
        isLoading,
        switchStore,
        resetToAssignedStore,
        storeOptions: STORE_OPTIONS,
    };

    return (
        <StoreContext.Provider value={value}>
            {children}
        </StoreContext.Provider>
    );
}

export function useStore(): StoreContextValue {
    const context = useContext(StoreContext);
    if (!context) {
        throw new Error('useStore must be used within a StoreProvider');
    }
    return context;
}

export default StoreContext;
